import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { 
  AlertTriangle, 
  Users, 
  Phone, 
  RefreshCw,
  Shield,
  Ban,
  ChevronRight,
  CheckCircle,
  XCircle,
  Eye,
  TrendingDown,
  Flame,
  Upload,
  Loader2,
  ExternalLink,
  Trash2,
  Brain,
  Sparkles,
  Clock,
  MapPin,
  Tag,
  BarChart3,
  FileText,
  Volume2,
  Calendar,
  Building,
  Target,
  Copy,
  Play,
  Pause,
  VolumeX,
  UserX,
  ToggleLeft,
  ToggleRight,
  Settings,
  Globe,
  Radio,
  ShoppingCart,
  X,
} from "lucide-react";
import DateFilter from "../components/DateFilter";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audio Player component for playing call recordings
const AudioPlayer = ({ recordingUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => {
      setError("Unable to load audio");
      setIsLoading(false);
    };
    const handleLoadStart = () => setIsLoading(true);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
  }, [recordingUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audio.currentTime = percent * duration;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!recordingUrl) {
    return (
      <div className="flex items-center justify-center p-4 bg-muted/30 rounded-lg">
        <p className="text-sm text-muted-foreground">No recording available</p>
      </div>
    );
  }

  return (
    <div className="bg-muted/30 rounded-lg p-4">
      <audio ref={audioRef} src={recordingUrl} preload="metadata" />
      
      {error ? (
        <div className="flex items-center justify-center p-2">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Progress bar */}
          <div 
            className="h-2 bg-muted rounded-full cursor-pointer overflow-hidden"
            onClick={handleSeek}
          >
            <div 
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            />
          </div>
          
          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9"
                onClick={togglePlay}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={toggleMute}
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            <div className="text-sm text-muted-foreground font-mono">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

import { PROBLEM_CATEGORIES, classifyCall } from "../lib/callFilters";

const TOGGLE_LABELS = {
  auto_block_adult: "Adult",
  auto_block_spam: "Spam / Fake",
  auto_block_dead: "Dead",
  auto_block_non_us: "Non-US Accent",
  auto_block_ivr: "IVR",
  auto_block_buy_sell: "Buy / Sell",
  auto_block_blacklisted_product: "Blacklisted Products",
  auto_pause_adult_publishers: "Pause Adult Publishers",
  auto_detect_behavioral_spam: "Behavioral Spam Detection",
};

const TOGGLE_CONFIG = {
  auto_block_adult: { color: "slate", icon: Ban, desc: "Auto-block callers flagged as adult content" },
  auto_block_spam: { color: "slate", icon: Shield, desc: "Auto-block callers flagged as spam or fake/fraud" },
  auto_block_dead: { color: "slate", icon: Phone, desc: "Auto-block callers with dead/silent calls" },
  auto_block_non_us: { color: "slate", icon: Globe, desc: "Auto-block callers with non-US accent" },
  auto_block_ivr: { color: "slate", icon: Radio, desc: "Auto-block IVR / robocaller detections" },
  auto_block_buy_sell: { color: "slate", icon: ShoppingCart, desc: "Auto-block callers with buy/sell lead intent" },
  auto_block_blacklisted_product: { color: "slate", icon: Ban, desc: "Auto-block callers mentioning a blacklisted product" },
  auto_pause_adult_publishers: { color: "slate", icon: UserX, desc: "Auto-pause publishers sending adult traffic" },
  auto_detect_behavioral_spam: { color: "slate", icon: Brain, desc: "Detect and block behavioral spam patterns" },
};

function computeActionData(calls, blockedNumbers) {
  const blockedSet = new Set(blockedNumbers);

  // --- Problem Publishers ---
  const pubMap = {};
  const adultCallerMap = {};
  const adultCallsList = [];
  const recentProblemCalls = [];

  for (const call of calls) {
    const cls = classifyCall(call);
    const pub = call.publisher_name || "Unknown";

    // Publisher aggregation
    if (!pubMap[pub]) pubMap[pub] = { name: pub, total: 0, valid: 0, fake: 0, adult: 0, problem_calls: [] };
    pubMap[pub].total++;
    if (cls === "valid") pubMap[pub].valid++;
    else if (cls === "problem") {
      if (call.category === "adult_call") pubMap[pub].adult++;
      else pubMap[pub].fake++;
      pubMap[pub].problem_calls.push(call);
    }

    // Adult callers (not blocked, not manual_valid)
    if (call.category === "adult_call" && !call.manual_valid) {
      adultCallsList.push(call);
      const num = call.caller_number;
      if (num && !blockedSet.has(num)) {
        if (!adultCallerMap[num]) adultCallerMap[num] = { caller_number: num, adult_calls: 0, publisher_name: pub, last_call: call.call_timestamp, keywords: [] };
        adultCallerMap[num].adult_calls++;
        if (call.keywords) adultCallerMap[num].keywords = [...new Set([...adultCallerMap[num].keywords, ...call.keywords])];
      }
    }

    // All problem calls for "Recent Alerts"
    if (cls === "problem") {
      recentProblemCalls.push(call);
    }
  }

  // Problem publishers: have adult OR high fake count OR low quality
  const allPubs = Object.values(pubMap);
  const problemPubs = allPubs
    .map(p => {
      const qualityScore = p.total > 0 ? Math.round((p.valid / p.total) * 100) : 0;
      return { ...p, quality_score: qualityScore, recent_problem_calls: p.problem_calls.slice(0, 3) };
    })
    .filter(p => p.adult > 0 || p.fake > 5 || p.quality_score < 50)
    .sort((a, b) => a.quality_score - b.quality_score)
    .slice(0, 5);

  const adultCallersList = Object.values(adultCallerMap).sort((a, b) => b.adult_calls - a.adult_calls);

  // Recent alerts = ALL problem calls sorted by most recent
  const alerts = recentProblemCalls
    .sort((a, b) => (b.call_timestamp || "").localeCompare(a.call_timestamp || ""))
    .map(call => ({
      id: call.id,
      publisher_name: call.publisher_name,
      caller_number: call.caller_number,
      alert_type: call.category,
      severity: call.category === "adult_call" ? "high" : call.category === "spam_call" ? "high" : "medium",
      call_duration: call.call_duration,
      call_timestamp: call.call_timestamp,
      created_at: call.call_timestamp,
      offer_name: call.offer_name || call.campaign_name,
      keywords: call.keywords || [],
      fraud_score: call.quality_score ? (100 - call.quality_score) : 50,
      summary: call.summary,
      recording_url: call.recording_url,
      transcript: call.transcript,
      category: call.category,
    }));

  const highSeverity = alerts.filter(a => a.severity === "high").length;

  return {
    problemPubs,
    adultCallersList,
    adultCallsList,
    alerts,
    stats: {
      totalAlerts: recentProblemCalls.length,
      highSeverity,
      problemPublishers: problemPubs.length,
      adultCallers: adultCallersList.length,
    },
  };
}

export default function ActionCenterPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [problemPublishers, setProblemPublishers] = useState([]);
  const [spamCallers, setSpamCallers] = useState([]);
  const [showAllBlocked, setShowAllBlocked] = useState(false);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingCallers, setSyncingCallers] = useState({});
  const [unblockingCallers, setUnblockingCallers] = useState({});
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [stats, setStats] = useState({
    totalAlerts: 0,
    highSeverity: 0,
    problemPublishers: 0,
    blockedCallers: 0,
    adultCallers: 0,
  });
  const [adultCallers, setAdultCallers] = useState([]);
  const [blockingCallers, setBlockingCallers] = useState({});
  const [adultAnalysis, setAdultAnalysis] = useState(null);
  const [analyzingAdult, setAnalyzingAdult] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [loadingAlertDetails, setLoadingAlertDetails] = useState(false);
  const [actionSettings, setActionSettings] = useState({
    auto_block_adult: true,
    auto_block_spam: true,
    auto_block_dead: false,
    auto_block_non_us: true,
    auto_block_ivr: true,
    auto_block_buy_sell: false,
    auto_block_blacklisted_product: false,
    auto_pause_adult_publishers: true,
    auto_detect_behavioral_spam: true,
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);
  // Blacklisted products state
  const [blacklistedProducts, setBlacklistedProducts] = useState([]);
  const [newProductName, setNewProductName] = useState("");
  const [productBusy, setProductBusy] = useState(false);

  // Fetch action settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/action-center/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setActionSettings(prev => ({ ...prev, ...data }));
        }
      } catch (e) {
        console.error("Failed to fetch action settings:", e);
      } finally {
        setSettingsLoading(false);
      }
    };
    if (token) fetchSettings();
  }, [token]);

  // Toggle a setting
  const toggleSetting = async (key) => {
    const newValue = !actionSettings[key];
    setTogglingKey(key);
    try {
      const res = await fetch(`${API_URL}/api/action-center/settings`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: newValue }),
      });
      if (res.ok) {
        const data = await res.json();
        setActionSettings(prev => ({ ...prev, ...data }));
        toast.success(`${newValue ? "Enabled" : "Disabled"}: ${TOGGLE_LABELS[key]}`);
      } else {
        toast.error("Failed to update setting");
      }
    } catch (e) {
      toast.error("Failed to update setting");
    } finally {
      setTogglingKey(null);
    }
  };

  // One-shot cleanup: unblock every caller that got auto-blocked on Sep 4, 2026
  // — the day before the strict-off toggle policy went live. Uses the admin
  // endpoint which flips DB state AND pushes the unblock to Ringba (rate-paced).
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const runSep4Cleanup = async () => {
    setCleanupBusy(true);
    setCleanupResult(null);
    try {
      const res = await fetch(
        `${API_URL}/api/action-center/unblock-auto-blocked-on-date?date=2026-09-04&push_to_ringba=true`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Cleanup failed");
      }
      setCleanupResult(data);
      if (data.unblocked_count === 0) {
        toast.info("No callers auto-blocked on Sep 4th were found.");
      } else {
        toast.success(
          `Unblocked ${data.unblocked_count} caller${data.unblocked_count === 1 ? "" : "s"} — Ringba: ${data.ringba_success} ok, ${data.ringba_failed} failed`
        );
      }
    } catch (e) {
      toast.error(e.message || "Cleanup failed");
    } finally {
      setCleanupBusy(false);
    }
  };

  // Blacklisted products — fetch on load
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/action-center/blacklisted-products`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBlacklistedProducts(data.products || []);
        }
      } catch (e) {
        console.error("Failed to fetch blacklisted products:", e);
      }
    };
    if (token) fetchProducts();
  }, [token]);

  const addBlacklistedProduct = async () => {
    const name = (newProductName || "").trim();
    if (!name) return;
    setProductBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/action-center/blacklisted-products`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const data = await res.json();
        setBlacklistedProducts(data.products || []);
        setNewProductName("");
        toast.success(`Added: ${data.added}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to add product");
      }
    } catch (e) {
      toast.error("Failed to add product");
    } finally {
      setProductBusy(false);
    }
  };

  const removeBlacklistedProduct = async (name) => {
    setProductBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/action-center/blacklisted-products/${encodeURIComponent(name)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBlacklistedProducts(data.products || []);
        toast.success(`Removed: ${name}`);
      } else {
        toast.error("Failed to remove product");
      }
    } catch (e) {
      toast.error("Failed to remove product");
    } finally {
      setProductBusy(false);
    }
  };

  const fetchActionItems = useCallback(async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;
    try {
      setLoading(true);

      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      params.set("start_date", dateRange.startDate);
      params.set("end_date", dateRange.endDate);
      params.set("limit", "10000");

      // Single source of truth: /api/calls
      const [callsRes, callersRes] = await Promise.all([
        fetch(`${API_URL}/api/calls?${params}`, { headers }),
        fetch(`${API_URL}/api/callers?status=blocked&limit=1000`, { headers }),
      ]);

      const callsData = await callsRes.json();
      const callersData = await callersRes.json();
      const calls = Array.isArray(callsData) ? callsData : [];
      const blocked = Array.isArray(callersData) ? callersData : [];
      setSpamCallers(blocked);

      const blockedNumbers = blocked.map(c => c.caller_number);
      const result = computeActionData(calls, blockedNumbers);

      setProblemPublishers(result.problemPubs);
      setAdultCallers(result.adultCallersList);
      setAlerts(result.alerts);
      setStats({ ...result.stats, blockedCallers: blocked.length });

      // Generate analysis for adult calls
      if (result.adultCallsList.length > 0) {
        generateAdultAnalysis(result.adultCallsList, result.adultCallersList);
      } else {
        setAdultAnalysis(null);
      }
    } catch (error) {
      console.error("Failed to fetch action items:", error);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  // Generate AI analysis for adult calls
  const generateAdultAnalysis = (adultCalls, callersList) => {
    // Analyze patterns
    const publisherCounts = {};
    const keywordCounts = {};
    const hourCounts = {};
    let totalCalls = adultCalls.length;
    
    adultCalls.forEach(call => {
      // Count by publisher
      const pub = call.publisher_name || 'Unknown';
      publisherCounts[pub] = (publisherCounts[pub] || 0) + 1;
      
      // Count keywords
      if (call.keywords) {
        call.keywords.forEach(kw => {
          keywordCounts[kw] = (keywordCounts[kw] || 0) + 1;
        });
      }
      
      // Count by hour
      if (call.call_timestamp) {
        const hour = new Date(call.call_timestamp).getHours();
        hourCounts[hour] = (hourCounts[hour] || 0) + 1;
      }
    });
    
    // Find top publishers
    const topPublishers = Object.entries(publisherCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name, count]) => ({ name, count, percent: Math.round((count / totalCalls) * 100) }));
    
    // Find top keywords
    const topKeywords = Object.entries(keywordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([keyword, count]) => keyword);
    
    // Find peak hours
    const peakHour = Object.entries(hourCounts)
      .sort((a, b) => b[1] - a[1])[0];
    const peakHourFormatted = peakHour ? `${peakHour[0] > 12 ? peakHour[0] - 12 : peakHour[0]}${peakHour[0] >= 12 ? 'PM' : 'AM'}` : 'N/A';
    
    // Calculate risk level
    const uniqueCallers = callersList.length;
    const repeatOffenders = callersList.filter(c => c.adult_calls > 1).length;
    const riskLevel = totalCalls > 20 ? 'High' : totalCalls > 5 ? 'Medium' : 'Low';
    
    // Generate recommendations
    const recommendations = [];
    if (topPublishers.length > 0 && topPublishers[0].percent > 30) {
      recommendations.push(`Review publisher "${topPublishers[0].name}" - responsible for ${topPublishers[0].percent}% of adult calls`);
    }
    if (repeatOffenders > 0) {
      recommendations.push(`${repeatOffenders} repeat offenders detected - consider immediate blocking`);
    }
    if (topKeywords.includes('escort') || topKeywords.includes('massage')) {
      recommendations.push(`High "escort" and "massage" keyword frequency - may indicate organized spam ring`);
    }
    if (uniqueCallers > 5) {
      recommendations.push(`${uniqueCallers} unique adult callers - use "Block All" to protect your campaigns`);
    }
    
    setAdultAnalysis({
      totalCalls,
      uniqueCallers,
      repeatOffenders,
      riskLevel,
      topPublishers,
      topKeywords,
      peakHour: peakHourFormatted,
      recommendations,
    });
  };

  // Run AI analysis on adult calls using GPT
  const runAIAnalysis = async () => {
    if (adultCallers.length === 0) {
      toast.info('No adult calls to analyze');
      return;
    }
    
    setAnalyzingAdult(true);
    try {
      const response = await fetch(`${API_URL}/api/analyze-adult-calls`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          caller_numbers: adultCallers.map(c => c.caller_number),
          start_date: dateRange.startDate,
          end_date: dateRange.endDate
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdultAnalysis(prev => ({
          ...prev,
          aiInsights: data.insights,
          aiRecommendations: data.recommendations
        }));
        toast.success('AI analysis complete');
      } else {
        toast.error('Failed to run AI analysis');
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      toast.error('Failed to run AI analysis');
    } finally {
      setAnalyzingAdult(false);
    }
  };

  // View alert details in modal
  const viewAlertDetails = (alert) => {
    setSelectedAlert(alert);
    setAlertModalOpen(true);
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    }
  };

  // Get category config
  const getCategoryConfig = (category) => {
    const configs = {
      adult_call: { label: "Adult Content", color: "bg-red-500/15 text-red-400", icon: Ban },
      spam_call: { label: "Spam/Fake", color: "bg-red-500/15 text-red-400", icon: XCircle },
      dead_call: { label: "Dead Call", color: "bg-slate-700/50 text-slate-400", icon: Phone },
      irrelevant_conversation: { label: "Irrelevant", color: "bg-slate-700/50 text-slate-400", icon: XCircle },
      valid_service_call: { label: "Valid", color: "bg-emerald-500/15 text-emerald-400", icon: CheckCircle },
    };
    return configs[category] || { label: category || "Unknown", color: "bg-slate-700/50 text-slate-400", icon: AlertTriangle };
  };

  useEffect(() => {
    fetchActionItems();
  }, [fetchActionItems]);

  const handleDateFilterChange = (filter) => {
    if (filter) {
      setDateRange(filter);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Sync a single blocked caller to Ringba
  const syncCallerToRingba = async (callerNumber) => {
    setSyncingCallers(prev => ({ ...prev, [callerNumber]: true }));
    try {
      const response = await fetch(`${API_URL}/api/callers/${encodeURIComponent(callerNumber)}/sync-to-ringba`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        toast.success(`${callerNumber} synced to Ringba successfully`);
        // Update local state to show synced status
        setSpamCallers(prev => prev.map(c => 
          c.caller_number === callerNumber 
            ? { ...c, ringba_synced: true, ringba_sync_time: new Date().toISOString() }
            : c
        ));
      } else if (data.status === 'skipped') {
        toast.info(data.message || 'Ringba integration not enabled');
      } else {
        toast.error(data.error || 'Failed to sync to Ringba');
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to sync to Ringba');
    } finally {
      setSyncingCallers(prev => ({ ...prev, [callerNumber]: false }));
    }
  };

  // Sync all blocked callers to Ringba
  const syncAllCallersToRingba = async () => {
    if (spamCallers.length === 0) {
      toast.info('No blocked callers to sync');
      return;
    }
    
    toast.info(`Syncing ${spamCallers.length} blocked callers to Ringba...`);
    
    for (const caller of spamCallers) {
      await syncCallerToRingba(caller.caller_number);
    }
    
    toast.success('Sync complete!');
  };

  // Unblock a caller
  const unblockCaller = async (callerNumber) => {
    setUnblockingCallers(prev => ({ ...prev, [callerNumber]: true }));
    try {
      const response = await fetch(`${API_URL}/api/callers/${encodeURIComponent(callerNumber)}/unblock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`${callerNumber} unblocked`);
        // Remove from local state
        setSpamCallers(prev => prev.filter(c => c.caller_number !== callerNumber));
        setStats(prev => ({ ...prev, blockedCallers: Math.max(0, prev.blockedCallers - 1) }));
      } else {
        toast.error(data.detail || 'Failed to unblock caller');
      }
    } catch (error) {
      console.error('Unblock error:', error);
      toast.error('Failed to unblock caller');
    } finally {
      setUnblockingCallers(prev => ({ ...prev, [callerNumber]: false }));
    }
  };

  // Block an adult caller
  const blockCaller = async (callerNumber) => {
    setBlockingCallers(prev => ({ ...prev, [callerNumber]: true }));
    try {
      const response = await fetch(`${API_URL}/api/callers/${encodeURIComponent(callerNumber)}/block`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`${callerNumber} blocked successfully`);
        // Remove from adult callers and add to blocked
        setAdultCallers(prev => prev.filter(c => c.caller_number !== callerNumber));
        setSpamCallers(prev => [...prev, { caller_number: callerNumber, status: 'blocked', spam_calls: 0 }]);
        setStats(prev => ({ 
          ...prev, 
          blockedCallers: prev.blockedCallers + 1,
          adultCallers: Math.max(0, prev.adultCallers - 1)
        }));
      } else {
        toast.error(data.detail || 'Failed to block caller');
      }
    } catch (error) {
      console.error('Block error:', error);
      toast.error('Failed to block caller');
    } finally {
      setBlockingCallers(prev => ({ ...prev, [callerNumber]: false }));
    }
  };

  // Block all adult callers
  const blockAllAdultCallers = async () => {
    if (adultCallers.length === 0) {
      toast.info('No adult callers to block');
      return;
    }
    
    toast.info(`Blocking ${adultCallers.length} adult callers...`);
    
    for (const caller of adultCallers) {
      await blockCaller(caller.caller_number);
    }
    
    toast.success('All adult callers blocked!');
  };

  // Auto-block ALL fraud callers and sync to Ringba
  const [autoBlocking, setAutoBlocking] = useState(false);
  const autoBlockAllFraudCallers = async () => {
    setAutoBlocking(true);
    try {
      const response = await fetch(`${API_URL}/api/callers/auto-block-all-fraud`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        toast.success(data.message);
        // Refresh the page data
        fetchActionItems();
      } else {
        toast.error(data.detail || 'Failed to auto-block callers');
      }
    } catch (error) {
      console.error('Auto-block error:', error);
      toast.error('Failed to auto-block callers');
    } finally {
      setAutoBlocking(false);
    }
  };

  // Auto-pause ALL adult publishers and sync to Ringba
  const [autoPausing, setAutoPausing] = useState(false);
  const autoPauseAdultPublishers = async () => {
    setAutoPausing(true);
    try {
      const response = await fetch(`${API_URL}/api/publishers/auto-pause-adult`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        toast.success(data.message);
        // Refresh the page data
        fetchActionItems();
      } else {
        toast.error(data.detail || 'Failed to auto-pause publishers');
      }
    } catch (error) {
      console.error('Auto-pause error:', error);
      toast.error('Failed to auto-pause publishers');
    } finally {
      setAutoPausing(false);
    }
  };

  // Detect behavioral spam across all callers
  const [detectingSpam, setDetectingSpam] = useState(false);
  const detectBehavioralSpam = async () => {
    setDetectingSpam(true);
    try {
      const response = await fetch(`${API_URL}/api/callers/detect-behavioral-spam`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        toast.success(data.message);
        fetchActionItems();
      } else {
        toast.error(data.detail || 'Failed to detect spam');
      }
    } catch (error) {
      console.error('Spam detection error:', error);
      toast.error('Failed to detect behavioral spam');
    } finally {
      setDetectingSpam(false);
    }
  };

  const getQualityColor = (score) => {
    if (score >= 70) return "bg-emerald-500";
    if (score >= 40) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getIssues = (pub) => {
    const issues = [];
    if ((pub.adult || 0) > 0) issues.push({ type: "adult", label: "Adult Content", color: "text-red-400" });
    if ((pub.fake || 0) > 5) issues.push({ type: "fake", label: "High Fake Calls", color: "text-red-400" });
    if ((pub.quality_score || 100) < 50) issues.push({ type: "quality", label: "Low Quality", color: "text-slate-400" });
    return issues;
  };

  // Calculate overall health score
  const healthScore = Math.max(0, 100 - (stats.highSeverity * 20) - (stats.problemPublishers * 10) - (stats.blockedCallers * 5));

  return (
    <div className="space-y-6" data-testid="action-center-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Action Center</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Issues requiring your attention
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              showSettings
                ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                : "bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:bg-slate-800 hover:text-slate-300"
            }`}
            data-testid="toggle-settings-btn"
          >
            <Settings className="h-3.5 w-3.5" />
            Auto-Actions
          </button>
          <button
            onClick={autoBlockAllFraudCallers}
            disabled={autoBlocking}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800/60 text-slate-300 border border-slate-700/50 hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50"
            data-testid="auto-block-all-btn"
          >
            {autoBlocking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shield className="h-3.5 w-3.5" />}
            Auto-Block Fraud
          </button>
          <button
            onClick={autoPauseAdultPublishers}
            disabled={autoPausing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800/60 text-slate-300 border border-slate-700/50 hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50"
            data-testid="auto-pause-publishers-btn"
          >
            {autoPausing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserX className="h-3.5 w-3.5" />}
            Pause Adult Pubs
          </button>
          <button
            onClick={detectBehavioralSpam}
            disabled={detectingSpam}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800/60 text-slate-300 border border-slate-700/50 hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50"
            data-testid="detect-spam-btn"
          >
            {detectingSpam ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            Detect Spam
          </button>
        </div>
      </div>

      {/* Auto-Action Toggle Panel */}
      {showSettings && (
        <Card className="bg-[#11141D] border-white/5 animate-in fade-in-0 slide-in-from-top-2 duration-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-200">Auto-Block Rules</span>
              <span className="text-[10px] text-slate-500 ml-1">ON = auto-action when detected | OFF = show in Call Analytics only</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {Object.entries(TOGGLE_CONFIG).map(([key, cfg]) => {
                const isOn = actionSettings[key];
                const isToggling = togglingKey === key;
                const Icon = cfg.icon;
                const colorMap = {
                  slate: { on: "bg-slate-700/20 border-slate-600/30 text-slate-300", dot: "bg-blue-500" },
                };
                const c = colorMap[cfg.color];
                return (
                  <button
                    key={key}
                    onClick={() => toggleSetting(key)}
                    disabled={isToggling || settingsLoading}
                    className={`relative flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-all ${
                      isOn ? c.on : "bg-slate-900/40 border-slate-800/50 text-slate-500"
                    } hover:brightness-110 disabled:opacity-50`}
                    data-testid={`toggle-${key}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{TOGGLE_LABELS[key]}</p>
                      <p className="text-[10px] text-slate-500 truncate mt-0.5">{cfg.desc}</p>
                    </div>
                    <div className={`relative w-9 h-5 rounded-full flex-shrink-0 transition-colors ${isOn ? c.dot : "bg-slate-700"}`}>
                      {isToggling ? (
                        <Loader2 className="h-3 w-3 animate-spin absolute top-1 left-3 text-white" />
                      ) : (
                        <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${isOn ? c.dot : "left-0.5"}`}
                          style={{ transform: isOn ? "translateX(16px)" : "translateX(0)" }}
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* ── Blacklisted Products management ── */}
            <div className="mt-5 pt-4 border-t border-white/5">
              <div className="flex items-center gap-2 mb-2">
                <Ban className="h-4 w-4 text-rose-400" />
                <span className="text-sm font-semibold text-slate-200">Blacklisted Products</span>
                <span className="text-[10px] text-slate-500 ml-1">Any call transcript mentioning these products is flagged as &quot;Blacklisted&quot;</span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="text"
                  value={newProductName}
                  onChange={(e) => setNewProductName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBlacklistedProduct(); } }}
                  placeholder='e.g. "Mini splits AC" or "Water heater"'
                  disabled={productBusy}
                  className="flex-1 rounded-lg bg-slate-900/60 border border-white/5 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-rose-500/40 disabled:opacity-50"
                  data-testid="blacklisted-product-input"
                />
                <button
                  onClick={addBlacklistedProduct}
                  disabled={productBusy || !newProductName.trim()}
                  className="rounded-lg bg-rose-600/80 hover:bg-rose-600 text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-40"
                  data-testid="blacklisted-product-add"
                >
                  Add
                </button>
              </div>
              {blacklistedProducts.length === 0 ? (
                <p className="text-xs text-slate-500 italic">No blacklisted products yet — add one above to start flagging matching calls.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {blacklistedProducts.map(product => (
                    <span
                      key={product}
                      className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 border border-rose-500/25 px-3 py-1 text-xs text-rose-300"
                      data-testid={`blacklisted-product-item-${product}`}
                    >
                      {product}
                      <button
                        onClick={() => removeBlacklistedProduct(product)}
                        disabled={productBusy}
                        className="ml-1 hover:text-white transition-colors disabled:opacity-40"
                        title={`Remove ${product}`}
                        data-testid={`blacklisted-product-remove-${product}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* ── One-shot Sep 4, 2026 cleanup: unblock callers auto-blocked ── */}
            {/* while the strict-off policy was rolling out. Safe to click     ── */}
            {/* multiple times — idempotent, only touches auto_blocked=True.   ── */}
            <div className="mt-5 pt-4 border-t border-white/5" data-testid="sep4-cleanup-section">
              <div className="flex items-center gap-2 mb-2">
                <RefreshCw className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-semibold text-slate-200">Cleanup: Sep 4th Auto-Blocks</span>
                <span className="text-[10px] text-slate-500 ml-1">
                  Reverses every caller auto-blocked on 2026-09-04 (before the strict-off policy) and pushes the unblock to Ringba.
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runSep4Cleanup}
                  disabled={cleanupBusy}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
                  data-testid="sep4-cleanup-btn"
                >
                  {cleanupBusy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Unblocking…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      Unblock Sep 4th Callers
                    </>
                  )}
                </Button>
                {cleanupResult && (
                  <div className="text-[11px] text-slate-400" data-testid="sep4-cleanup-result">
                    Last run: <span className="text-slate-200 font-medium">{cleanupResult.unblocked_count}</span> unblocked
                    <span className="text-slate-600 mx-1.5">·</span>
                    Ringba: <span className="text-emerald-400">{cleanupResult.ringba_success} ok</span>
                    {cleanupResult.ringba_failed > 0 && (
                      <>
                        <span className="text-slate-600 mx-1.5">·</span>
                        <span className="text-rose-400">{cleanupResult.ringba_failed} failed</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="bg-[#11141D] border border-white/5 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <DateFilter onFilterChange={handleDateFilterChange} defaultPreset="today" compact showTimezone={false} />
          </div>
          <button
            onClick={fetchActionItems}
            disabled={loading}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-2.5 py-1.5 transition-all shrink-0"
            data-testid="refresh-actions"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      {/* Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Health Score */}
        <Card className="bg-[#11141D] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Health Score</p>
                <p className={`text-3xl font-bold mt-1.5 ${healthScore >= 70 ? 'text-emerald-400' : healthScore >= 40 ? 'text-yellow-400' : 'text-red-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {healthScore}%
                </p>
                <p className="text-[11px] text-slate-500 mt-1">
                  {healthScore >= 70 ? "Good standing" : healthScore >= 40 ? "Needs attention" : "Critical issues"}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${healthScore >= 70 ? 'bg-emerald-500/10 border border-emerald-500/15' : healthScore >= 40 ? 'bg-yellow-500/10 border border-yellow-500/15' : 'bg-red-500/10 border border-red-500/15'}`}>
                {healthScore >= 70 ? <CheckCircle className="h-6 w-6 text-emerald-400" /> : healthScore >= 40 ? <AlertTriangle className="h-6 w-6 text-yellow-400" /> : <Flame className="h-6 w-6 text-red-400" />}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Adult Callers */}
        <Card className="bg-[#11141D] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Adult Callers</p>
                <p className="text-3xl font-bold text-white mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.adultCallers}</p>
                <p className="text-[11px] text-slate-600 mt-1">Need blocking</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700/40"><Ban className="h-6 w-6 text-slate-400" /></div>
            </div>
          </CardContent>
        </Card>

        {/* High Severity Alerts */}
        <Card className="bg-[#11141D] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">High Severity</p>
                <p className="text-3xl font-bold text-white mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.highSeverity}</p>
                <p className="text-[11px] text-slate-600 mt-1">Urgent alerts</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700/40"><Shield className="h-6 w-6 text-slate-400" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Problem Publishers */}
        <Card className="bg-[#11141D] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Problem Pubs</p>
                <p className="text-3xl font-bold text-white mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.problemPublishers}</p>
                <p className="text-[11px] text-slate-600 mt-1">Need review</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700/40"><Users className="h-6 w-6 text-slate-400" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Blocked Callers */}
        <Card className="bg-[#11141D] border-white/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Blocked Callers</p>
                <p className="text-3xl font-bold text-white mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.blockedCallers}</p>
                <p className="text-[11px] text-slate-600 mt-1">Spam numbers</p>
              </div>
              <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700/40"><Ban className="h-6 w-6 text-slate-400" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Adult Callers Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ban className="h-4 w-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Adult Content Callers
            </h2>
            {adultCallers.length > 0 && (
              <Badge className="bg-slate-800 text-slate-300 border-0 text-[10px] px-1.5 py-0">{adultCallers.length}</Badge>
            )}
          </div>
          {adultCallers.length > 0 && (
            <button
              onClick={blockAllAdultCallers}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-all"
            >
              <Ban className="h-3.5 w-3.5" />
              Block All
            </button>
          )}
        </div>

        {adultCallers.length === 0 ? (
          <Card className="bg-[#11141D] border-white/5">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-10 w-10 mx-auto mb-3 text-emerald-500/40" />
              <p className="text-sm font-medium text-slate-300">No Adult Callers Detected</p>
              <p className="text-xs text-slate-500 mt-1">All adult callers have been blocked or none found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2">
            {adultCallers.slice(0, 10).map((caller, idx) => (
              <div key={caller.caller_number || idx} className="flex items-center justify-between bg-[#11141D] border border-white/5 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-800/60">
                    <Phone className="h-4 w-4 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-mono text-sm font-semibold text-slate-200">{caller.caller_number}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-[11px] text-slate-500">
                        {caller.publisher_name || 'Unknown'}
                      </p>
                      {caller.keywords && caller.keywords.length > 0 && (
                        <Badge className="text-[10px] bg-slate-800 text-slate-400 border-0 px-1.5 py-0">
                          {caller.keywords.slice(0, 2).join(', ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{caller.adult_calls}</p>
                    <p className="text-[10px] text-slate-500">Adult</p>
                  </div>
                  <button
                    onClick={() => blockCaller(caller.caller_number)}
                    disabled={blockingCallers[caller.caller_number]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-all disabled:opacity-50"
                  >
                    {blockingCallers[caller.caller_number] ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <><Ban className="h-3.5 w-3.5" /> Block</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Analysis for Adult Calls */}
      {(adultCallers.length > 0 || adultAnalysis) && (
        <Card className="bg-[#11141D] border-white/5">
          <CardHeader className="pb-3 border-b border-white/5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-200 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Brain className="h-4 w-4 text-slate-400" />
                AI Analysis - Adult Content Patterns
              </CardTitle>
              <button
                onClick={runAIAnalysis}
                disabled={analyzingAdult || adultCallers.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800/60 text-slate-300 border border-slate-700/50 hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50"
              >
                {analyzingAdult ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
                Run Deep Analysis
              </button>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {adultAnalysis ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{adultAnalysis.totalCalls}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Adult Calls</p>
                  </div>
                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{adultAnalysis.uniqueCallers}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Unique Callers</p>
                  </div>
                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-500" style={{ fontFamily: 'Outfit, sans-serif' }}>{adultAnalysis.repeatOffenders}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Repeat Offenders</p>
                  </div>
                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-3 text-center">
                    <Badge className={`text-sm px-2.5 py-0.5 ${
                      adultAnalysis.riskLevel === 'High' ? 'bg-red-500/10 text-red-400' :
                      adultAnalysis.riskLevel === 'Medium' ? 'bg-yellow-500/10 text-yellow-400' :
                      'bg-emerald-500/10 text-emerald-400'
                    } border-0`}>
                      {adultAnalysis.riskLevel} Risk
                    </Badge>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Risk Level</p>
                  </div>
                </div>

                {/* Insights Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-4">
                    <h4 className="font-semibold text-xs text-slate-400 flex items-center gap-2 mb-3 uppercase tracking-wider">
                      <Users className="h-3.5 w-3.5 text-slate-500" />
                      Top Publishers
                    </h4>
                    {adultAnalysis.topPublishers && adultAnalysis.topPublishers.length > 0 ? (
                      <div className="space-y-2">
                        {adultAnalysis.topPublishers.map((pub, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="truncate text-slate-400">{pub.name}</span>
                            <Badge className="bg-slate-800 text-slate-300 border-0 text-[10px] px-1.5 py-0">
                              {pub.percent}%
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">No data</p>
                    )}
                  </div>

                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-4">
                    <h4 className="font-semibold text-xs text-slate-400 flex items-center gap-2 mb-3 uppercase tracking-wider">
                      <Tag className="h-3.5 w-3.5 text-slate-500" />
                      Flagged Keywords
                    </h4>
                    {adultAnalysis.topKeywords && adultAnalysis.topKeywords.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {adultAnalysis.topKeywords.map((kw, i) => (
                          <Badge key={i} className="bg-red-500/10 text-red-400 border-0 text-[10px] px-1.5 py-0">
                            {kw}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">No keywords detected</p>
                    )}
                  </div>

                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-4">
                    <h4 className="font-semibold text-xs text-slate-400 flex items-center gap-2 mb-3 uppercase tracking-wider">
                      <Clock className="h-3.5 w-3.5 text-slate-500" />
                      Peak Activity
                    </h4>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{adultAnalysis.peakHour}</p>
                    <p className="text-[10px] text-slate-500">Most common call time</p>
                  </div>
                </div>

                {/* AI Recommendations */}
                {adultAnalysis.recommendations && adultAnalysis.recommendations.length > 0 && (
                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-4">
                    <h4 className="font-semibold text-xs text-slate-400 flex items-center gap-2 mb-3 uppercase tracking-wider">
                      <Sparkles className="h-3.5 w-3.5 text-slate-500" />
                      Recommendations
                    </h4>
                    <ul className="space-y-2">
                      {adultAnalysis.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <AlertTriangle className="h-3.5 w-3.5 text-slate-500 shrink-0 mt-0.5" />
                          <span className="text-slate-400">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* AI Deep Insights */}
                {adultAnalysis.aiInsights && (
                  <div className="bg-[#0B0D12] border border-white/5 rounded-lg p-4">
                    <h4 className="font-semibold text-xs text-slate-400 flex items-center gap-2 mb-3 uppercase tracking-wider">
                      <Brain className="h-3.5 w-3.5 text-slate-500" />
                      Deep AI Insights
                    </h4>
                    <p className="text-xs text-slate-400 whitespace-pre-wrap leading-relaxed">{adultAnalysis.aiInsights}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-6">
                <BarChart3 className="h-7 w-7 mx-auto mb-2 text-slate-600" />
                <p className="text-xs text-slate-500">No adult call data to analyze</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Problem Publishers Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Publishers Requiring Attention
          </h2>
          {problemPublishers.length > 0 && (
            <Badge className="bg-slate-800 text-slate-300 border-0 text-[10px] px-1.5 py-0">{problemPublishers.length}</Badge>
          )}
        </div>

        {loading ? (
          <Card className="bg-[#11141D] border-white/5">
            <CardContent className="p-8 text-center">
              <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-slate-500" />
              <p className="text-xs text-slate-500">Loading...</p>
            </CardContent>
          </Card>
        ) : problemPublishers.length === 0 ? (
          <Card className="bg-[#11141D] border-white/5">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-10 w-10 mx-auto mb-3 text-emerald-500/40" />
              <p className="text-sm font-medium text-slate-300">All Publishers Healthy</p>
              <p className="text-xs text-slate-500 mt-1">No publishers require attention at this time</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {problemPublishers.slice(0, 5).map((pub, idx) => {
              const issues = getIssues(pub);
              const qualityScore = pub.quality_score || 0;
              
              return (
                <Card 
                  key={pub.id || pub.publisher_id || idx}
                  className="bg-[#11141D] border-white/5"
                >
                  <CardContent className="p-5">
                    {/* Header Row */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-slate-800/60 shrink-0">
                          <Users className="h-5 w-5 text-slate-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {pub.name || "Unknown Publisher"}
                          </h3>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {issues.map((issue, i) => (
                              <Badge 
                                key={i} 
                                className={`text-[10px] px-1.5 py-0 border-0 ${
                                  issue.type === 'adult' ? 'bg-red-500/10 text-red-400' :
                                  issue.type === 'fake' ? 'bg-red-500/10 text-red-400' :
                                  'bg-slate-800 text-slate-400'
                                }`}
                              >
                                {issue.label}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/call-analysis?publisher=${encodeURIComponent(pub.name)}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-white hover:bg-white/5 border border-white/5 transition-all"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View Calls
                      </button>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-5 gap-3 mb-4 p-3 bg-[#0B0D12] border border-white/5 rounded-lg">
                      <div className="text-center">
                        <p className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{pub.total || 0}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-emerald-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{pub.valid || 0}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Valid</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{pub.fake || 0}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Fake</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-purple-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{pub.adult || 0}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Adult</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Progress value={qualityScore} className="w-14 h-1.5" />
                          <span className={`text-xs font-bold ${qualityScore >= 70 ? 'text-emerald-400' : qualityScore >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {qualityScore}%
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Quality</p>
                      </div>
                    </div>

                    {/* Recent Problem Calls */}
                    {pub.recent_problem_calls && pub.recent_problem_calls.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Recent Problem Calls</p>
                        <div className="space-y-1">
                          {pub.recent_problem_calls.slice(0, 3).map((call, ci) => (
                            <div key={ci} className="flex items-center justify-between p-2 bg-red-500/[0.03] border border-red-500/10 rounded-lg">
                              <div className="flex items-center gap-2.5">
                                <Phone className="h-3.5 w-3.5 text-slate-500" />
                                <div>
                                  <p className="text-xs font-mono text-slate-300">{call.caller_number}</p>
                                  <p className="text-[10px] text-slate-500">{call.offer_name || call.campaign_name || 'No offer'}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={`text-[10px] px-1.5 py-0 border-0 ${
                                  call.category === 'adult_call' ? 'bg-red-500/10 text-red-400' :
                                  call.category === 'spam_call' ? 'bg-red-500/10 text-red-400' :
                                  'bg-slate-800 text-slate-400'
                                }`}>
                                  {call.category === 'adult_call' ? 'Adult' : 
                                   call.category === 'spam_call' ? 'Fake' : 
                                   call.category || 'Problem'}
                                </Badge>
                                <span className="text-[10px] text-slate-500">
                                  {call.call_timestamp ? new Date(call.call_timestamp).toLocaleDateString() : 'N/A'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Blocked Callers Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Blocked Spam Callers
            </h2>
            {spamCallers.length > 0 && (
              <Badge className="bg-slate-800 text-slate-300 border-0 text-[10px] px-1.5 py-0">{spamCallers.length}</Badge>
            )}
          </div>
          {spamCallers.length > 0 && (
            <button
              onClick={syncAllCallersToRingba}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800/60 text-slate-300 border border-slate-700/50 hover:bg-slate-700/60 hover:text-white transition-all"
            >
              <Upload className="h-3.5 w-3.5" />
              Sync All to Ringba
            </button>
          )}
        </div>

        {spamCallers.length === 0 ? (
          <Card className="bg-[#11141D] border-white/5">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-10 w-10 mx-auto mb-3 text-emerald-500/40" />
              <p className="text-sm font-medium text-slate-300">No Blocked Callers</p>
              <p className="text-xs text-slate-500 mt-1">No spam callers have been blocked yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2">
            {spamCallers.slice(0, showAllBlocked ? spamCallers.length : 10).map((caller, idx) => (
              <div key={caller.id || caller.caller_number || idx} className="flex items-center justify-between bg-[#11141D] border border-white/5 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <Ban className="h-4 w-4 text-red-400" />
                  </div>
                  <div>
                    <p className="font-mono text-sm font-semibold text-slate-200">{caller.caller_number}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <p className="text-[10px] text-slate-500">
                        Blocked {formatDate(caller.blocked_at || caller.updated_at)}
                      </p>
                      {caller.auto_blocked && (
                        <Badge className="text-[10px] bg-slate-800 text-slate-400 border-0 px-1.5 py-0">Auto</Badge>
                      )}
                      {caller.ringba_synced && (
                        <Badge className="text-[10px] bg-emerald-500/10 text-emerald-400 border-0 px-1.5 py-0">Synced</Badge>
                      )}
                      {caller.blocked_reason && (
                        <Badge className={`text-[10px] border-0 px-1.5 py-0 ${
                          caller.blocked_reason === 'adult_call' ? 'bg-red-500/10 text-red-400' :
                          caller.blocked_reason === 'dead_call' ? 'bg-slate-800 text-slate-400' :
                          caller.blocked_reason === 'behavioral_spam' ? 'bg-slate-800 text-slate-400' :
                          'bg-red-500/10 text-red-400'
                        }`}>{
                          caller.blocked_reason === 'spam_call' ? 'Spam' :
                          caller.blocked_reason === 'adult_call' ? 'Adult' :
                          caller.blocked_reason === 'dead_call' ? 'Dead Call' :
                          caller.blocked_reason === 'behavioral_spam' ? 'Behavioral Spam' :
                          caller.blocked_reason === 'ivr_call' ? 'IVR/Bot' :
                          caller.blocked_reason
                        }</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-sm font-bold text-white">{caller.total_calls || 0}</p>
                    <p className="text-[10px] text-slate-500">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-red-400">{caller.spam_calls || 0}</p>
                    <p className="text-[10px] text-slate-500">Spam</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {!caller.ringba_synced && (
                      <button
                        onClick={() => syncCallerToRingba(caller.caller_number)}
                        disabled={syncingCallers[caller.caller_number]}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-slate-800/60 text-slate-300 border border-slate-700/50 hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50"
                      >
                        {syncingCallers[caller.caller_number] ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Upload className="h-3 w-3" /> Sync</>}
                      </button>
                    )}
                    <button
                      onClick={() => unblockCaller(caller.caller_number)}
                      disabled={unblockingCallers[caller.caller_number]}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-slate-800/60 text-slate-300 border border-slate-700/50 hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50"
                    >
                      {unblockingCallers[caller.caller_number] ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Trash2 className="h-3 w-3" /> Unblock</>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {spamCallers.length > 10 && (
              <button
                onClick={() => setShowAllBlocked(!showAllBlocked)}
                className="w-full py-2 text-xs font-semibold text-slate-400 hover:text-slate-200 bg-[#11141D] border border-white/5 rounded-xl transition-colors"
                data-testid="show-all-blocked-btn"
              >
                {showAllBlocked ? `Show Less` : `View All ${spamCallers.length} Blocked Callers`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Recent Alerts Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-slate-400" />
          <h2 className="text-sm font-semibold text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Recent Fraud Alerts
          </h2>
          {alerts.length > 0 && (
            <Badge className="bg-slate-800 text-slate-300 border-0 text-[10px] px-1.5 py-0">{alerts.length}</Badge>
          )}
        </div>

        {alerts.length === 0 ? (
          <Card className="bg-[#11141D] border-white/5">
            <CardContent className="p-8 text-center">
              <Shield className="h-10 w-10 mx-auto mb-3 text-emerald-500/40" />
              <p className="text-sm font-medium text-slate-300">No Fraud Alerts</p>
              <p className="text-xs text-slate-500 mt-1">Your call traffic is clean!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2">
            {alerts.slice(0, showAllAlerts ? alerts.length : 10).map((alert, idx) => {
              const catConfig = getCategoryConfig(alert.alert_type);
              const borderColor = alert.severity === 'high' ? 'border-l-red-500/60' : alert.severity === 'medium' ? 'border-l-slate-500' : 'border-l-slate-700';
              return (
                <div
                  key={alert.id || idx}
                  className={`bg-[#11141D] border border-white/5 border-l-2 ${borderColor} rounded-xl p-4 cursor-pointer hover:bg-white/[0.02] transition-all`}
                  onClick={() => viewAlertDetails(alert)}
                >
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg shrink-0 ${catConfig.color}`}>
                        <catConfig.icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-sm text-slate-200">
                            {alert.publisher_name || "Unknown Publisher"}
                          </h3>
                          <Badge className={`text-[10px] px-1.5 py-0 border-0 ${catConfig.color}`}>{catConfig.label}</Badge>
                          <Badge className={`text-[10px] px-1.5 py-0 border-0 ${
                            alert.severity === 'high' ? 'bg-red-500/10 text-red-400' : 
                            alert.severity === 'medium' ? 'bg-slate-800 text-slate-300' : 
                            'bg-slate-800 text-slate-400'
                          }`}>
                            {alert.severity === 'high' ? 'High' : alert.severity === 'medium' ? 'Medium' : 'Low'}
                          </Badge>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">{formatDate(alert.created_at)}</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); viewAlertDetails(alert); }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-slate-400 hover:text-white hover:bg-white/5 border border-white/5 transition-all shrink-0"
                    >
                      <Eye className="h-3 w-3" /> Details
                    </button>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-2.5 bg-[#0B0D12] border border-white/5 rounded-lg">
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Caller</p>
                      <p className="font-mono text-xs text-slate-300 mt-0.5">{alert.caller_number || "---"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Offer</p>
                      <p className="text-xs text-slate-300 mt-0.5 truncate">{alert.offer_name || alert.campaign_name || "---"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Duration</p>
                      <p className="text-xs text-slate-300 mt-0.5">{formatDuration(alert.call_duration)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Fraud Score</p>
                      <p className={`text-xs font-bold mt-0.5 ${
                        (alert.fraud_score || 0) >= 70 ? 'text-red-400' : (alert.fraud_score || 0) >= 40 ? 'text-yellow-400' : 'text-emerald-400'
                      }`}>{alert.fraud_score || 0}%</p>
                    </div>
                  </div>

                  {/* Keywords */}
                  {alert.keywords && alert.keywords.length > 0 && (
                    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-slate-500">Keywords:</span>
                      {alert.keywords.slice(0, 5).map((kw, i) => (
                        <Badge key={i} className="text-[10px] bg-red-500/10 text-red-400 border-0 px-1.5 py-0">{kw}</Badge>
                      ))}
                      {alert.keywords.length > 5 && (
                        <span className="text-[10px] text-slate-500">+{alert.keywords.length - 5} more</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {alerts.length > 10 && (
              <button
                onClick={() => setShowAllAlerts(!showAllAlerts)}
                className="w-full py-2 text-xs font-semibold text-slate-400 hover:text-slate-200 bg-[#11141D] border border-white/5 rounded-xl transition-colors"
                data-testid="show-all-alerts-btn"
              >
                {showAllAlerts ? `Show Less` : `View All ${alerts.length} Alerts`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Fraud Alert Details Modal */}
      <Dialog open={alertModalOpen} onOpenChange={setAlertModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0B0D12] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <AlertTriangle className="h-4 w-4 text-slate-400" />
              Fraud Alert Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedAlert && (
            <div className="space-y-6 mt-4">
              {/* Classification Badge */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Classification</span>
                <div className="flex items-center gap-2">
                  <Badge className={getCategoryConfig(selectedAlert.alert_type).color}>
                    {getCategoryConfig(selectedAlert.alert_type).label}
                  </Badge>
                  <Badge className={`${
                    selectedAlert.severity === 'high' ? 'bg-red-500/10 text-red-400' : 
                    selectedAlert.severity === 'medium' ? 'bg-slate-800 text-slate-300' : 
                    'bg-slate-800 text-slate-400'
                  }`}>
                    {selectedAlert.severity === 'high' ? 'High Priority' : 
                     selectedAlert.severity === 'medium' ? 'Medium Priority' : 'Low Priority'}
                  </Badge>
                </div>
              </div>

              {/* Call Information */}
              <Card className="bg-[#11141D] border-white/5">
                <CardHeader className="pb-3 border-b border-white/5">
                  <CardTitle className="text-xs font-semibold text-slate-400 flex items-center gap-2 uppercase tracking-wider">
                    <Phone className="h-3.5 w-3.5 text-slate-500" />
                    Call Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Caller Number</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-300">{selectedAlert.caller_number || "---"}</span>
                        {selectedAlert.caller_number && (
                          <button className="text-slate-500 hover:text-white transition" onClick={() => copyToClipboard(selectedAlert.caller_number)}>
                            <Copy className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Publisher</span>
                      <span className="text-xs text-slate-300">{selectedAlert.publisher_name || "---"}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Offer/Campaign</span>
                      <span className="text-sm">{selectedAlert.offer_name || selectedAlert.campaign_name || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Call Duration</span>
                      <span className="text-sm">{formatDuration(selectedAlert.call_duration)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Call Date</span>
                      <span className="text-sm">{formatDate(selectedAlert.call_timestamp || selectedAlert.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Fraud Score</span>
                      <span className={`text-sm font-bold ${
                        (selectedAlert.fraud_score || 0) >= 70 ? 'text-red-500' :
                        (selectedAlert.fraud_score || 0) >= 40 ? 'text-yellow-500' :
                        'text-emerald-500'
                      }`}>
                        {selectedAlert.fraud_score || 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Flagged Keywords */}
              {selectedAlert.keywords && selectedAlert.keywords.length > 0 && (
                <Card className="bg-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Tag className="h-4 w-4 text-red-400" />
                      Flagged Keywords (from Caller)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {selectedAlert.keywords.map((kw, i) => (
                        <Badge key={i} className="bg-red-500/20 text-red-400 border-0">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Audio Player */}
              <Card className="bg-muted/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Volume2 className="h-4 w-4 text-slate-400" />
                    Call Recording
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AudioPlayer recordingUrl={selectedAlert.recording_url} />
                </CardContent>
              </Card>

              {/* Summary */}
              <Card className="bg-muted/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-400" />
                    Caller's Request Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed">
                    {selectedAlert.summary || selectedAlert.description || "No summary available."}
                  </p>
                </CardContent>
              </Card>

              {/* Transcript if available */}
              {selectedAlert.transcript && (
                <Card className="bg-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      Call Transcript
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-60 overflow-y-auto">
                      <p className="text-sm font-mono whitespace-pre-wrap bg-background p-3 rounded-lg">
                        {selectedAlert.transcript}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Problem Calls from this Publisher */}
              {selectedAlert.problem_calls && selectedAlert.problem_calls.length > 0 && (
                <Card className="bg-muted/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-slate-400" />
                      Problem Calls from this Publisher ({selectedAlert.problem_calls.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedAlert.problem_calls.slice(0, 5).map((call, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-background rounded-lg">
                          <div className="flex items-center gap-3">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="font-mono text-sm">{call.caller_number}</p>
                              <p className="text-xs text-muted-foreground">{call.offer_name || call.campaign_name || 'No offer'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getCategoryConfig(call.category).color}>
                              {getCategoryConfig(call.category).label}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{formatDuration(call.call_duration)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/call-analysis?publisher=${encodeURIComponent(selectedAlert.publisher_name)}`)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View All Publisher Calls
                </Button>
                {selectedAlert.caller_number && (
                  <Button
                    variant="default"
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => {
                      blockCaller(selectedAlert.caller_number);
                      setAlertModalOpen(false);
                    }}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    Block This Caller
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
